import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import removeOptionColorValue from '@repo/utils/removeOptionColorValue';
import { useUiComponentsInternationalContext } from '@repo/static-translations';

import styles from '../styles';
import MeasureIcon from '../svgs/measure-icon.svg';
import breakpoints from '../breakpoints';

const DropDownContainer = styled.div`
  label {
    span {
      display: block;
      margin-bottom: 0.25rem;
    }
  }

  select {
    font-size: ${styles.fontSize.base};
    padding: 0.5rem 1rem 0.25rem 0;
  }
`;
const HowToMeasureContainer = styled.a`
  display: flex;
  align-items: center;
  margin-bottom: 0.25rem;
  margin-left: 0.25rem;

  svg {
    flex-grow: 0;
    flex-shrink: 0;
    margin-right: 0.5rem;
  }
`;

const Heading = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;

  @media (min-width: ${breakpoints.lg}) {
    width: 50%;
    white-space: nowrap;
  }
`;

function Dropdown({
  name,
  values,
  activeValue,
  variants,
  findVariantByOptions,
  activeSelectedOptions,
  optionChangeHandler,
  hasSizeChart,
  measureIconColor,
}) {
  const { tt } = useUiComponentsInternationalContext();
  // as we loop modify activeSelectedOptions single value to match what we are looping over
  // so we can find matched variant
  function updateValueOnOptions(v) {
    // Updated: activeSelectedOptions should be using options of current variant, not the first variant
    // as it might be missing options
    const findVariantWithOption = variants.find(variant =>
      variant.selectedOptions.find(opt => opt.name === name && opt.value === v)
    );

    const findActiveSelectedOption = activeSelectedOptions.find(
      so => so.name === name && so.value === v
    );

    if (findActiveSelectedOption) {
      return activeSelectedOptions?.map(so => {
        const copy = { ...so };
        if (copy.name === name) {
          copy.value = v;
        }
        return copy;
      });
    }

    if (findVariantWithOption) {
      return findVariantWithOption.selectedOptions?.map(so => {
        const copy = { ...so };
        if (copy.name === name) {
          copy.value = v;
        }
        return copy;
      });
    }
    return undefined;
  }

  const options = values.map(optionValue => {
    const matchedVariant = findVariantByOptions(
      variants,
      updateValueOnOptions(optionValue)
    );

    return {
      value: matchedVariant?.sku,
      label: removeOptionColorValue(optionValue),
    };
  });

  const handleChange = (newOption, opt) => {
    if (opt?.action === 'clear') {
      optionChangeHandler(null);
    }
    optionChangeHandler(newOption?.value);
  };

  return (
    <DropDownContainer>
      <label htmlFor={`field-${name}`}>
        <Heading>
          <span>{name}</span>
          {hasSizeChart && (
            <HowToMeasureContainer href="#SizeChart">
              <MeasureIcon width="25" height="25" fill={measureIconColor} />{' '}
              {tt(s => s.productCommon.howToMeasureLink)}
            </HowToMeasureContainer>
          )}
        </Heading>
        <label htmlFor={`field-${name}`}>
          <select
            id={`field-${name}`}
            name={name}
            placeholder={tt(s => s.productCommon.selectNamePlaceholder, {
              name,
            })}
            defaultValue={options.find(o => o.value === activeValue)?.value}
            onChange={event =>
              handleChange(
                options.find(o => o.value === event.target.value),
                { action: '' }
              )
            }
            className="product-option-select"
          >
            <option>
              {tt(s => s.productCommon.selectNameOption, { name })}
            </option>
            {options?.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      </label>
    </DropDownContainer>
  );
}

Dropdown.propTypes = {
  name: PropTypes.string.isRequired,
  values: PropTypes.arrayOf(PropTypes.string).isRequired,
  variants: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  findVariantByOptions: PropTypes.func.isRequired,
  optionChangeHandler: PropTypes.func.isRequired,
  activeSelectedOptions: PropTypes.arrayOf(
    PropTypes.shape({ name: PropTypes.string, value: PropTypes.string })
  ),
  activeValue: PropTypes.string,
  hasSizeChart: PropTypes.bool,
  measureIconColor: PropTypes.string,
};

Dropdown.defaultProps = {
  activeValue: '',
  hasSizeChart: false,
  measureIconColor: '#000',
  activeSelectedOptions: null,
};

export default Dropdown;
